import { FormEvent, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input } from "antd";
import { Lock } from "lucide-react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { BrandLogo } from "@/components/shared/BrandLogo";
import { Seo } from "@/components/seo/Seo";
import { routePaths } from "@/routes/routePaths";
import { getAdminMe, loginAdmin } from "@/services/adminApi";

export function AdminLoginPage() {
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const cachedMe = queryClient.getQueryData<
    Awaited<ReturnType<typeof getAdminMe>>
  >(["admin", "me"]);
  const from =
    typeof location.state?.from === "string"
      ? location.state.from
      : routePaths.admin.root;

  const loginMutation = useMutation({
    mutationFn: loginAdmin,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
      navigate(from, { replace: true });
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    loginMutation.mutate({ password });
  }

  if (cachedMe?.authenticated) {
    return <Navigate to={routePaths.admin.root} replace />;
  }

  return (
    <>
      <Seo
        path={routePaths.admin.login}
        title="Admin Login | VideoTranscriptSaver"
        description="Admin login for VideoTranscriptSaver."
        robots="noindex,nofollow"
      />
      <main className="flex min-h-screen items-center justify-center bg-white px-4 py-10 text-neutral-950 dark:bg-ink-900 dark:text-neutral-50">
        <section className="w-full max-w-sm border border-neutral-200 p-5 dark:border-neutral-800 sm:p-6">
          <BrandLogo />
          <div className="mt-6">
            <h1 className="text-2xl font-semibold">Admin login</h1>
          </div>
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <Input.Password
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Admin password"
              autoComplete="current-password"
              size="large"
              aria-label="Admin password"
            />
            {loginMutation.error ? (
              <Alert
                type="error"
                showIcon
                message={loginMutation.error.message}
                className="rounded-none"
              />
            ) : null}
            <Button
              htmlType="submit"
              type="primary"
              size="large"
              loading={loginMutation.isPending}
              icon={<Lock className="h-4 w-4" />}
              className="w-full rounded-none bg-neutral-950 font-medium dark:bg-white dark:text-neutral-950"
            >
              Log in
            </Button>
          </form>
        </section>
      </main>
    </>
  );
}
